import { useState } from 'react'
import { toast } from 'sonner'
import { Pencil, Truck, Info } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useEditShipment } from '@/api/hooks'
import type { components } from '@/api/schema'
import { formatApiErrorMessage } from '@/lib/api-error'
import { ShipmentCustomerNotificationPolicyFields } from '@/components/shipment-customer-notification-policy'
import { shipmentCustomerNotificationPolicy } from '@/lib/shipment-customer-notification-policy'

type Shipment = components['schemas']['PurchaseOrderShipmentRead']
type EditBody = components['schemas']['EditShipmentRequest']

interface EditShipmentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  poUuid: string
  shipment: Shipment
  policyOnly?: boolean
}

function initialFormFromShipment(s: Shipment) {
  return {
    status: s.status === 'ready_to_book' ? 'ready_to_book' : 'pending',
    booked_by: s.booked_by ?? 'us',
    pickup_date: s.pickup_date ?? '',
    pickup_time_start: s.pickup_time_start ?? '',
    pickup_time_end: s.pickup_time_end ?? '',
    liftgate_pickup: s.liftgate_pickup ?? false,
    inside_pickup: s.inside_pickup ?? false,
    residential_pickup: s.residential_pickup ?? false,
    liftgate_required: s.liftgate_required ?? false,
    appointment_required: s.appointment_required ?? false,
    inside_delivery: s.inside_delivery ?? false,
    residential_delivery: s.residential_delivery ?? false,
    notify_before_delivery: s.notify_before_delivery ?? false,
  }
}

type FormState = ReturnType<typeof initialFormFromShipment>

function buildBody(form: FormState, source: Shipment): EditBody {
  const body: EditBody = {}

  if (form.status !== source.status) {
    body.status = form.status
  }
  if (form.booked_by !== (source.booked_by ?? 'us')) {
    body.booked_by = form.booked_by
  }

  const stringFields: Array<[keyof FormState, keyof EditBody]> = [
    ['pickup_time_start', 'pickup_time_start'],
    ['pickup_time_end', 'pickup_time_end'],
  ]
  for (const [formKey, bodyKey] of stringFields) {
    const newValue = form[formKey] as string
    const oldValue = (source[bodyKey as keyof Shipment] ?? '') as string
    if (newValue !== oldValue) {
      ;(body as Record<string, unknown>)[bodyKey] = newValue || null
    }
  }

  if (form.pickup_date !== (source.pickup_date ?? '')) {
    body.pickup_date = form.pickup_date || null
  }

  const boolFields: Array<keyof EditBody & keyof FormState> = [
    'liftgate_pickup',
    'inside_pickup',
    'residential_pickup',
    'liftgate_required',
    'appointment_required',
    'inside_delivery',
    'residential_delivery',
    'notify_before_delivery',
  ]
  for (const k of boolFields) {
    if (form[k] !== (source[k as keyof Shipment] ?? false)) {
      ;(body as Record<string, unknown>)[k] = form[k]
    }
  }

  return body
}

// The saved text details edit in place, a section at a time (USA-1580); each section PATCHes only
// the fields that changed, with the same empty-means-null rules the sheet's Save uses.
type TextKey =
  | 'ship_from_name'
  | 'ship_from_contact_name'
  | 'ship_from_email'
  | 'ship_from_phone'
  | 'ship_from_address1'
  | 'ship_from_address2'
  | 'ship_from_city'
  | 'ship_from_state'
  | 'ship_from_zip'
  | 'total_weight_lbs'
  | 'pallet_count'
  | 'freight_class'
  | 'shipping_notes'

type AddressKey = Extract<TextKey, `ship_from_${string}`>
type CargoKey = 'total_weight_lbs' | 'pallet_count' | 'freight_class'

const ADDRESS_FIELDS: ReadonlyArray<InlineGroupField<AddressKey>> = [
  { key: 'ship_from_name', label: 'Company' },
  { key: 'ship_from_contact_name', label: 'Contact name' },
  { key: 'ship_from_phone', label: 'Phone', inputMode: 'tel', className: 'col-span-3' },
  { key: 'ship_from_email', label: 'Email', inputMode: 'email', className: 'col-span-3' },
  { key: 'ship_from_address1', label: 'Address line 1' },
  { key: 'ship_from_address2', label: 'Address line 2' },
  { key: 'ship_from_city', label: 'City', className: 'col-span-3' },
  { key: 'ship_from_state', label: 'State', className: 'col-span-1' },
  { key: 'ship_from_zip', label: 'ZIP', inputMode: 'numeric', className: 'col-span-2' },
]

const CARGO_FIELDS: ReadonlyArray<InlineGroupField<CargoKey>> = [
  {
    key: 'total_weight_lbs',
    label: 'Total weight (lbs)',
    inputMode: 'decimal',
    className: 'col-span-2',
  },
  { key: 'pallet_count', label: 'Pallet count', inputMode: 'numeric', className: 'col-span-2' },
  { key: 'freight_class', label: 'Freight class', className: 'col-span-2' },
]

const NOTES_FIELDS: ReadonlyArray<InlineGroupField<'shipping_notes'>> = [
  { key: 'shipping_notes', label: 'Shipping notes', kind: 'textarea' },
]

function savedText(s: Shipment): Record<TextKey, string> {
  return {
    ship_from_name: s.ship_from_name ?? '',
    ship_from_contact_name: s.ship_from_contact_name ?? '',
    ship_from_email: s.ship_from_email ?? '',
    ship_from_phone: s.ship_from_phone ?? '',
    ship_from_address1: s.ship_from_address1 ?? '',
    ship_from_address2: s.ship_from_address2 ?? '',
    ship_from_city: s.ship_from_city ?? '',
    ship_from_state: s.ship_from_state ?? '',
    ship_from_zip: s.ship_from_zip ?? '',
    total_weight_lbs: s.total_weight_lbs != null ? String(s.total_weight_lbs) : '',
    pallet_count: s.pallet_count != null ? String(s.pallet_count) : '',
    freight_class: s.freight_class ?? '',
    shipping_notes: s.shipping_notes ?? '',
  }
}

function textBody(draft: Partial<Record<TextKey, string>>, source: Shipment): EditBody {
  const body: EditBody = {}
  const saved = savedText(source)
  for (const key of Object.keys(draft) as TextKey[]) {
    const newValue = draft[key] ?? ''
    if (newValue === saved[key]) continue
    if (key === 'total_weight_lbs') {
      body.total_weight_lbs = newValue ? newValue : null
    } else if (key === 'pallet_count') {
      body.pallet_count = newValue ? Number(newValue) : null
    } else {
      ;(body as Record<string, unknown>)[key] = newValue || null
    }
  }
  return body
}

function SavedDetailsGroup<K extends TextKey>({
  label,
  fields,
  poUuid,
  shipment,
  labelled = false,
  invalid,
  notice,
  editing,
  onEditingChange,
  children,
}: {
  label: string
  fields: ReadonlyArray<InlineGroupField<K>>
  poUuid: string
  shipment: Shipment
  labelled?: boolean
  invalid?: (draft: Record<K, string>) => ReadonlyArray<K>
  notice?: (draft: Record<K, string>) => React.ReactNode
  editing: boolean
  onEditingChange: (editing: boolean) => void
  children: React.ReactNode
}) {
  const editMutation = useEditShipment(poUuid, shipment.uuid)
  const setEditing = onEditingChange
  const saved = savedText(shipment)
  const values = Object.fromEntries(fields.map((field) => [field.key, saved[field.key]])) as Record<
    K,
    string
  >

  const save = (next: Record<K, string>) => {
    const body = textBody(next, shipment)
    if (Object.keys(body).length === 0) {
      setEditing(false)
      return
    }
    editMutation.mutate(
      { shipmentId: shipment.id, body },
      {
        onSuccess: () => {
          toast.success('Shipment updated')
          setEditing(false)
        },
        onError: (err) => {
          toast.error(formatApiErrorMessage('Save failed', err))
        },
      },
    )
  }

  return (
    <InlineFieldGroup
      label={label}
      fields={fields}
      values={values}
      labelled={labelled}
      invalid={invalid}
      notice={notice}
      pending={editMutation.isPending}
      editing={editing}
      onEditingChange={setEditing}
      onSave={save}
    >
      {children}
    </InlineFieldGroup>
  )
}

function AddressReadView({ shipment }: { shipment: Shipment }) {
  const s = savedText(shipment)
  const stateZip = [s.ship_from_state, s.ship_from_zip].filter(Boolean).join(' ')
  const cityLine = [s.ship_from_city, stateZip].filter(Boolean).join(', ')
  const lines = [
    s.ship_from_contact_name,
    [s.ship_from_phone, s.ship_from_email].filter(Boolean).join(' · '),
    s.ship_from_address1,
    s.ship_from_address2,
    cityLine,
  ].filter(Boolean)
  return (
    <span className="block space-y-0.5 text-sm">
      {s.ship_from_name ? (
        <span className="block font-medium">{s.ship_from_name}</span>
      ) : (
        <span className="block text-muted-foreground">No company set</span>
      )}
      {lines.map((line, index) => (
        <span key={index} className="block">
          {line}
        </span>
      ))}
      {!s.ship_from_phone && (
        <span className="block text-xs text-muted-foreground">
          No phone. Required for carrier booking.
        </span>
      )}
    </span>
  )
}

function CargoReadView({ shipment }: { shipment: Shipment }) {
  const s = savedText(shipment)
  const weight = Number(s.total_weight_lbs)
  const facts: Array<[string, string]> = [
    [
      'Total weight',
      s.total_weight_lbs
        ? `${Number.isFinite(weight) ? weight.toLocaleString() : s.total_weight_lbs} lbs`
        : 'Not set. Required for booking',
    ],
    ['Pallets', s.pallet_count || 'None (small parcel)'],
    ['Freight class', s.freight_class || 'Not set'],
  ]
  return (
    <span className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
      {facts.map(([name, value]) => (
        <span key={name} className="block">
          <span className="block text-xs text-muted-foreground">{name}</span>
          <span className="block tabular-nums">{value}</span>
        </span>
      ))}
    </span>
  )
}

export function EditShipmentSheet({
  open,
  onOpenChange,
  poUuid,
  shipment,
  policyOnly = false,
}: EditShipmentSheetProps) {
  const editMutation = useEditShipment(poUuid, shipment.uuid)
  const [form, setForm] = useState<FormState>(() => initialFormFromShipment(shipment))
  const sourceCustomerEmailPolicy = shipmentCustomerNotificationPolicy(
    shipment.dropship_accessorial_snapshot,
    shipment.dropship_accessorial_override,
  )
  const [customerEmailPolicy, setCustomerEmailPolicy] = useState(sourceCustomerEmailPolicy)
  // The sheet's Save waits while a section is open, so typed details are never left behind.
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(new Set())
  const sectionEditing = (name: string) => ({
    editing: openSections.has(name),
    onEditingChange: (editing: boolean) =>
      setOpenSections((prev) => {
        const next = new Set(prev)
        if (editing) next.add(name)
        else next.delete(name)
        return next
      }),
  })
  const sectionOpen = openSections.size > 0

  const handleSubmit = () => {
    if (sectionOpen) return
    const body = policyOnly ? ({} as EditBody) : buildBody(form, shipment)
    if (customerEmailPolicy.receiving_method !== sourceCustomerEmailPolicy.receiving_method) {
      body.receiving_method = customerEmailPolicy.receiving_method || null
    }
    if (customerEmailPolicy.damage_policy !== sourceCustomerEmailPolicy.damage_policy) {
      body.damage_policy = customerEmailPolicy.damage_policy || null
    }
    if (customerEmailPolicy.detention_policy !== sourceCustomerEmailPolicy.detention_policy) {
      body.detention_policy = customerEmailPolicy.detention_policy || null
    }
    if (customerEmailPolicy.detention_rate !== sourceCustomerEmailPolicy.detention_rate) {
      body.detention_rate = customerEmailPolicy.detention_rate || null
    }
    if (Object.keys(body).length === 0) {
      toast.message('No changes to save')
      return
    }
    editMutation.mutate(
      { shipmentId: shipment.id, body },
      {
        onSuccess: () => {
          const willBook = body.status === 'ready_to_book' && shipment.status !== 'ready_to_book'
          toast.success(
            willBook
              ? 'Shipment saved — booking queued, label/BOL email will follow'
              : 'Shipment updated',
          )
          onOpenChange(false)
        },
        onError: (err) => {
          toast.error(formatApiErrorMessage('Save failed', err))
        },
      },
    )
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const willBook =
    form.status === 'ready_to_book' &&
    shipment.status !== 'ready_to_book' &&
    form.booked_by === 'us'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-4 py-4 sm:px-6">
          <SheetTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            {policyOnly ? 'Customer delivery email' : 'Edit shipment'}
          </SheetTitle>
          <SheetDescription>
            {policyOnly ? (
              "Confirm the receiving and damage instructions used in this shipment's customer email."
            ) : (
              <>
            Update shipment details before it&apos;s booked. Setting status to{' '}
            <strong>Ready to Book</strong> with <strong>booked_by = us</strong> queues automatic
                carrier booking.
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {!policyOnly && willBook && (
          <div className="mx-4 mt-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30 sm:mx-6">
            <div className="flex items-start gap-2">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <p className="text-xs text-blue-800 dark:text-blue-200">
                Saving with status <strong>Ready to Book</strong> will queue the booking
                immediately. The carrier (WWEX) requires a pickup phone number and weight — make
                sure those fields are filled or booking will fail.
              </p>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <div className="space-y-6 px-4 py-4 sm:px-6">
            {!policyOnly && (
            <FormSection title="Status">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Shipment status">
                  <Select value={form.status} onValueChange={(value) => setField('status', value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending (gathering info)</SelectItem>
                      <SelectItem value="ready_to_book">Ready to Book</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Booked by">
                  <Select value={form.booked_by} onValueChange={(value) => setField('booked_by', value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us">Us (we book the carrier)</SelectItem>
                      <SelectItem value="manufacturer">Manufacturer (they ship direct)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </FormSection>
            )}

            {!policyOnly && <Separator />}

            {!policyOnly && (
            <FormSection title="Pickup address">
              <SavedDetailsGroup
                label="Pickup address"
                fields={ADDRESS_FIELDS}
                {...sectionEditing('address')}
                poUuid={poUuid}
                shipment={shipment}
                labelled
                invalid={(draft) => (draft.ship_from_state.length > 2 ? ['ship_from_state'] : [])}
                notice={(draft) =>
                  draft.ship_from_state.length > 2
                    ? 'State is the two-letter code, e.g. TX.'
                    : 'Phone is required for carrier booking.'
                }
              >
                <AddressReadView shipment={shipment} />
              </SavedDetailsGroup>
            </FormSection>
            )}

            {!policyOnly && <Separator />}

            {!policyOnly && (
            <FormSection title="Pickup window">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Date">
                  <Input
                    type="date"
                    value={form.pickup_date}
                    onChange={(e) => setField('pickup_date', e.target.value)}
                  />
                </Field>
                <Field label="Open">
                  <Input
                    type="time"
                    value={form.pickup_time_start}
                    onChange={(e) => setField('pickup_time_start', e.target.value)}
                  />
                </Field>
                <Field label="Close">
                  <Input
                    type="time"
                    value={form.pickup_time_end}
                    onChange={(e) => setField('pickup_time_end', e.target.value)}
                  />
                </Field>
              </div>
            </FormSection>
            )}

            {!policyOnly && <Separator />}

            {!policyOnly && (
            <FormSection title="Cargo">
              <SavedDetailsGroup
                label="Cargo"
                fields={CARGO_FIELDS}
                {...sectionEditing('cargo')}
                poUuid={poUuid}
                shipment={shipment}
                labelled
                notice={() =>
                  'Weight is required for booking. Leave pallets blank for small parcel.'
                }
              >
                <CargoReadView shipment={shipment} />
              </SavedDetailsGroup>

              {shipment.handling_units && shipment.handling_units.length > 0 && (
                <div className="rounded-md border bg-muted/30 px-3 py-2.5">
                    <p className="text-xs font-medium text-muted-foreground">
                      Handling units (preserved)
                    </p>
                  <ul className="mt-1.5 space-y-1 text-xs">
                    {shipment.handling_units.map((hu) => (
                      <li key={hu.id} className="flex flex-wrap gap-x-3 gap-y-1">
                        <span className="font-medium">
                          {hu.num_units}× {hu.unit_type}
                        </span>
                        {hu.commodities?.map((c) => (
                          <span key={c.id} className="text-muted-foreground">
                            {c.description} — {Number(c.weight_lbs).toLocaleString()} lbs
                            {c.pieces > 1 ? ` (${c.pieces} pcs)` : ''}
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </FormSection>
            )}

            {!policyOnly && <Separator />}

            <FormSection title={policyOnly ? 'Email policy' : 'Accessorials'}>
              {!policyOnly && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <ToggleRow
                  label="Liftgate at pickup"
                  checked={form.liftgate_pickup}
                  onCheckedChange={(v) => setField('liftgate_pickup', v)}
                />
                <ToggleRow
                  label="Inside pickup"
                  checked={form.inside_pickup}
                  onCheckedChange={(v) => setField('inside_pickup', v)}
                />
                <ToggleRow
                  label="Residential pickup"
                  checked={form.residential_pickup}
                  onCheckedChange={(v) => setField('residential_pickup', v)}
                />
                <ToggleRow
                  label="Liftgate at delivery"
                  checked={form.liftgate_required}
                  onCheckedChange={(v) => setField('liftgate_required', v)}
                />
                <ToggleRow
                  label="Inside delivery"
                  checked={form.inside_delivery}
                  onCheckedChange={(v) => setField('inside_delivery', v)}
                />
                <ToggleRow
                  label="Residential delivery"
                  checked={form.residential_delivery}
                  onCheckedChange={(v) => setField('residential_delivery', v)}
                />
                <ToggleRow
                  label="Appointment required"
                  checked={form.appointment_required}
                  onCheckedChange={(v) => setField('appointment_required', v)}
                />
                <ToggleRow
                  label="Notify before delivery"
                  checked={form.notify_before_delivery}
                  onCheckedChange={(v) => setField('notify_before_delivery', v)}
                />
              </div>
              )}
              <ShipmentCustomerNotificationPolicyFields
                value={customerEmailPolicy}
                onChange={setCustomerEmailPolicy}
              />
            </FormSection>

            {!policyOnly && <Separator />}

            {!policyOnly && (
            <FormSection title="Notes">
              <SavedDetailsGroup
                label="Notes"
                fields={NOTES_FIELDS}
                {...sectionEditing('notes')}
                poUuid={poUuid}
                shipment={shipment}
              >
                <span className="block whitespace-pre-wrap text-sm">
                  {shipment.shipping_notes || (
                    <span className="text-muted-foreground">
                      No notes. Add special handling, gate codes, etc.
                    </span>
                  )}
                </span>
              </SavedDetailsGroup>
            </FormSection>
            )}
          </div>
        </ScrollArea>

        <div className="flex flex-col-reverse gap-2 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-end sm:px-6">
          {sectionOpen && (
            <p className="text-xs text-muted-foreground sm:mr-auto">
              Save or cancel the open section first.
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={editMutation.isPending}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={editMutation.isPending}
            loadingText="Saving..."
            disabled={sectionOpen}
            onClick={handleSubmit}
          >
            {willBook ? (
              <>
                <Truck className="mr-1.5 h-3.5 w-3.5" />
                Save & Book
              </>
            ) : (
              <>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Save changes
              </>
            )}
          </LoadingButton>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2.5">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function ToggleRow({
  label,
  checked,
  onCheckedChange,
}: {
  label: string
  checked: boolean
  onCheckedChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border px-3 py-2 hover:bg-muted/30">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </label>
  )
}
